import Header from "@/components/layouts/Header";
import ReportHeader from "@/components/ReportHeader";
import DateField from "@/components/ui/date-field";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import ReferencePointSelect from "@/components/ui/reference-point-select";
import { ReportButton } from "@/components/ui/report-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import UserSelect from "@/components/ui/user-select";
import VehicleSelect from "@/components/ui/vehicle-select";
import { errorHandler, toggleVisibility } from "@/lib/utils";
import {
  trackReportsService,
  Visit,
  VisitsAnalysisData,
  VisitsHeatmapData,
  VisitsObjectiveSummary,
} from "@/services/api/trackReports";
import { visitObjectiveService } from "@/services/api/visitObjectives";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import VisitsReportAnalysis from "./VisitsReportAnalysis";
import VisitsReportHeatmap from "./VisitsReportHeatmap";
import VisitsObjectivesCharts from "./VisitsObjectivesCharts";
import VisitsReportTable from "./VisitsReportTable";

const formSchema = z.object({
  filter: z.enum(["vehicle", "reference_point"]),
  vehicle_id: z.number().int().positive().optional(),
  user_id: z.number().int().positive().optional(),
  reference_point_id: z.number().int().positive().optional(),
  start_date: z.string().date("Data inicial é obrigatória"),
  end_date: z.string().date("Data final é obrigatória"),
  separation: z.enum(["date", "reference_point"], {
    error: "Separação é obrigatória",
  }),
  count_all_daily_visits: z.boolean().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const VisitsReport = () => {
  const [isFormVisible, setIsFormVisible] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [visitsData, setVisitsData] = useState<
    { title: string; visits: Visit[] }[]
  >([]);
  const [analysisData, setAnalysisData] = useState<VisitsAnalysisData>();
  const [heatmapData, setHeatmapData] = useState<VisitsHeatmapData>();
  const [objectivesSummary, setObjectivesSummary] = useState<
    VisitsObjectiveSummary[]
  >([]);
  const [headerTitle, setHeaderTitle] = useState("");
  const [activeTab, setActiveTab] = useState("report");
  const [selectedObjectiveId, setSelectedObjectiveId] = useState<
    number | "all" | "no-objective"
  >("all");
  const [hasVisitObjectives, setHasVisitObjectives] = useState(false);

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      vehicle_id: undefined,
      user_id: undefined,
      start_date: format(firstDayOfMonth, "yyyy-MM-dd"),
      end_date: format(today, "yyyy-MM-dd"),
      separation: "date",
      filter: "vehicle",
      count_all_daily_visits: false,
    },
  });

  const onSubmit = async (data: FormValues) => {
    setStartDate(data.start_date);
    setEndDate(data.end_date);
    try {
      const reportPromise = trackReportsService.getVisitsReport(data);

      let heatmapPromise = null;
      if (data.filter === "vehicle" && data.vehicle_id) {
        heatmapPromise = trackReportsService.getVisitsHeatmap({
          vehicle_id: data.vehicle_id,
          start_date: data.start_date,
          end_date: data.end_date,
        });
      }

      // O mapa de calor é complementar: se ele falhar, o relatório ainda deve
      // abrir. Por isso allSettled em vez de all, que derrubaria os dois.
      const [reportResult, heatmapResult] = await Promise.allSettled([
        reportPromise,
        heatmapPromise,
      ]);

      if (reportResult.status === "rejected") {
        throw reportResult.reason;
      }

      const response = reportResult.value;
      const heatmapResponse =
        heatmapResult.status === "fulfilled" ? heatmapResult.value : null;

      if (!response.errors && response.data) {
        const allVisits: { title: string; visits: Visit[] }[] = [];

        // Process visits by dates
        Object.values(response.data.dates).forEach((dateData) => {
          allVisits.push(dateData);
        });

        // Process visits by reference points
        Object.values(response.data.reference_points).forEach(
          (refPointData) => {
            allVisits.push(refPointData);
          },
        );

        if (form.watch("filter") === "vehicle") {
          setHeaderTitle(
            `${response.data.vehicle_plate} ${response.data.vehicle_title}`,
          );
        } else {
          setHeaderTitle(response.data.reference_point ?? "");
        }

        setVisitsData(allVisits);
        setObjectivesSummary(response.data.objectives_summary ?? []);
        setSelectedObjectiveId("all");
        setAnalysisData(response.data.analysis ?? undefined);
        setHeatmapData(heatmapResponse?.data ?? undefined);
        setIsFormVisible(false);
        setActiveTab("report");
      } else {
        console.error("Error in response:", response.errors);
        // Handle error state here
      }
    } catch (error) {
      errorHandler(error, "Erro ao buscar relatório.");
      console.log("🚀 ~ onSubmit ~ error:", error);
    }
  };

  const handleToggleFormVisibility = () => {
    toggleVisibility(isFormVisible, setIsFormVisible);
  };

  const handleExportPdf = async () => {
    setPdfLoading(true);
    try {
      const params = {
        vehicle_id: form.getValues("vehicle_id"),
        user_id: form.getValues("user_id"),
        reference_point_id: form.getValues("reference_point_id"),
        start_date: startDate,
        end_date: endDate,
        filter: form.getValues("filter"),
        separation: form.getValues("separation"),
        count_all_daily_visits: form.getValues("count_all_daily_visits"),
      };

      const response = await trackReportsService.getVisitsReportPdf(params);

      const pdfBlob = new Blob([response], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl);
    } catch (error) {
      errorHandler(error, "Erro ao gerar PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  const allObjectives = useMemo(() => {
    return objectivesSummary
      .filter((summary) => summary.objective_id != null)
      .map((summary) => ({
        id: summary.objective_id as number,
        title: summary.objective_title || "Sem objetivo",
      }));
  }, [objectivesSummary]);

  const hasVisitsWithoutObjective = useMemo(
    () => objectivesSummary.some((summary) => summary.objective_id == null),
    [objectivesSummary],
  );

  const chartObjectives = useMemo(() => {
    if (selectedObjectiveId === "all") {
      return objectivesSummary;
    }

    if (selectedObjectiveId === "no-objective") {
      return objectivesSummary.filter(
        (summary) => summary.objective_id == null,
      );
    }

    return objectivesSummary.filter(
      (summary) => summary.objective_id === selectedObjectiveId,
    );
  }, [objectivesSummary, selectedObjectiveId]);

  useEffect(() => {
    let cancelled = false;

    const checkVisitObjectives = async () => {
      try {
        const response = await visitObjectiveService.index({
          page: 1,
          per_page: 3,
          search: "",
          filters: {},
        });

        if (cancelled) return;

        const hasAny = Array.isArray(response.data) && response.data.length > 0;
        setHasVisitObjectives(hasAny);
      } catch (error) {
        if (cancelled) return;
        setHasVisitObjectives(false);
      }
    };

    void checkVisitObjectives();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="h-full">
      <Header
        title="Visitas"
        showToggleButton
        isFormVisible={isFormVisible}
        onToggleFormVisibility={handleToggleFormVisibility}
      />

      <div
        className={`max-w-md transition-all duration-300 ease-in-out ${
          isFormVisible ? "opacity-100" : "opacity-0 max-h-0 overflow-hidden"
        }`}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="filter"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Filtrar por</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Selecione o filtro" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vehicle">Veículo</SelectItem>
                      <SelectItem value="reference_point">
                        Ponto de referência/cliente
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.watch("filter") === "vehicle" && (
              <>
                <FormField
                  control={form.control}
                  name="vehicle_id"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Veículo</FormLabel>
                      <VehicleSelect
                        value={field.value}
                        onChange={field.onChange}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="user_id"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>
                        Usuário (em caso de visitas manuais)
                      </FormLabel>
                      <UserSelect
                        value={field.value}
                        onChange={field.onChange}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {form.watch("filter") === "reference_point" && (
              <FormField
                control={form.control}
                name="reference_point_id"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Ponto de referência/cliente</FormLabel>
                    <ReferencePointSelect
                      value={field.value}
                      onChange={field.onChange}
                      showType="non-headquarters"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DateField form={form} name="start_date" label="De" />

            <DateField form={form} name="end_date" label="Até" />

            {form.watch("filter") === "vehicle" && (
              <FormField
                control={form.control}
                name="separation"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Separar por</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Selecione a separação" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date">Data</SelectItem>
                        <SelectItem value="reference_point">
                          Ponto de referência
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="count_all_daily_visits"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div
                    className="space-y-0.5 hover:cursor-pointer"
                    onClick={() => field.onChange(!field.value)}
                  >
                    <FormLabel
                      className="text-base hover:cursor-pointer"
                      htmlFor="enable_notifications"
                    >
                      Contabilizar todas visitas
                    </FormLabel>
                    <FormDescription>
                      Visitas que ocorrem no mesmo dia no mesmo ponto são
                      contabilizadas separadamente.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      className="bg-primary-500"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <ReportButton loading={form.formState.isSubmitting} />
          </form>
        </Form>
      </div>

      {!isFormVisible && (
        <div
          className={`w-full flex flex-col gap-4 transition-all duration-300 ease-in-out ${
            isFormVisible ? "opacity-0 max-h-0 overflow-hidden" : "opacity-100"
          }`}
        >
          {/* Cabecalho do relatorio */}
          <ReportHeader
            handleExportPdf={handleExportPdf}
            primaryInfo={headerTitle}
            secondaryInfo={`${startDate?.split("-").reverse().join("/")} • ${endDate?.split("-").reverse().join("/")}`}
            loading={pdfLoading}
          />

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full flex flex-col gap-4"
          >
            {form.watch("filter") === "vehicle" && (
              <TabsList className="w-fit bg-slate-100 dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700">
                <TabsTrigger
                  value="report"
                  className="px-6 py-1.5 rounded-md transition-all data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:shadow-xs"
                >
                  Relatório
                </TabsTrigger>
                <TabsTrigger
                  value="heatmap"
                  className="px-6 py-1.5 rounded-md transition-all data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:shadow-xs"
                >
                  Mapa
                </TabsTrigger>
              </TabsList>
            )}

            <TabsContent value="report" className="mt-0 space-y-4">
              {analysisData && <VisitsReportAnalysis data={analysisData} />}

              {/* Filtro de objetivo da visita (frontend only) */}
              {visitsData.length > 0 && hasVisitObjectives && (
                <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-xs border border-slate-100 dark:border-slate-700 flex flex-col gap-2">
                  <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                    Objetivo da visita
                  </span>
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <Select
                      value={String(selectedObjectiveId)}
                      onValueChange={(value) => {
                        if (value === "all") {
                          setSelectedObjectiveId("all");
                        } else if (value === "no-objective") {
                          setSelectedObjectiveId("no-objective");
                        } else {
                          setSelectedObjectiveId(Number(value));
                        }
                      }}
                    >
                      <SelectTrigger className="w-full sm:w-80">
                        <SelectValue placeholder="Selecione um objetivo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os objetivos</SelectItem>
                        {hasVisitsWithoutObjective && (
                          <SelectItem value="no-objective">
                            Visitas sem objetivo
                          </SelectItem>
                        )}
                        {allObjectives.map((objective) => (
                          <SelectItem
                            key={objective.id}
                            value={String(objective.id)}
                          >
                            {objective.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Gráficos por objetivo */}
              {objectivesSummary.length > 0 && hasVisitObjectives && (
                <VisitsObjectivesCharts objectives={chartObjectives} />
              )}

              {/* Tabela com dados do relatorio */}
              <VisitsReportTable
                visitsData={visitsData}
                filter={form.watch("filter")}
                separation={form.watch("separation")}
              />
            </TabsContent>

            {form.watch("filter") === "vehicle" && heatmapData && (
              <TabsContent value="heatmap" className="mt-0">
                <VisitsReportHeatmap data={heatmapData} />
              </TabsContent>
            )}
          </Tabs>
        </div>
      )}
    </div>
  );
};

export default VisitsReport;
